import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatDate } from "../helper/DateHelper";
import StandardDatePicker from "./StandardDatePicker";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { getStallBookedList, downloadStallBookedExcel } from "../Apis/StallEnquiryAPIs";
import { getExposList } from "../Apis/ExpoAPIs";
import { getManagers } from "../Apis/ManagerAPIs";
import { toast } from "react-toastify";
import TablePagination from "./TablePagination";
import { getImageUrl } from "../Apis/api";
import { Tooltip } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import usePermissions from "../hook/userPermission";
import { getClientFollowupPayments } from "../Apis/ClientAPIs";

const StallBookingListLayer = () => {
  const { hasPermission } = usePermissions();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ total: 0, full: 0, partial: 0, pending: 0 });
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBookingForView, setSelectedBookingForView] = useState(null);
  const [bookingPayments, setBookingPayments] = useState([]);

  // Dropdown data
  const [expoOptions, setExpoOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);

  // Filters
  const [selectedExpo, setSelectedExpo] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);

  // Load expo & manager options on mount
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [expoRes, manRes] = await Promise.all([
          getExposList({ limit: 1000 }),
          getManagers({ limit: 1000, isActive: true }),
        ]);
        if (expoRes?.status) {
          const expos = expoRes.response?.data || expoRes.response || [];
          setExpoOptions(
            expos.map((e) => ({
              value: e._id || e.id,
              label: e.expoName || e.name,
            }))
          );
        }
        if (manRes?.status) {
          const managers = manRes.response?.data || manRes.response || [];
          setManagerOptions(
            managers.map((m) => ({
              value: m._id || m.id,
              label: m.name,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to load filter dropdowns:", err);
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]',
    );
    const tooltipList = [...tooltipTriggerList].map((tooltipTriggerEl) => {
      return new Tooltip(tooltipTriggerEl);
    });
    return () => {
      tooltipList.forEach((tooltip) => tooltip.dispose());
    };
  }, [bookings, loading]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        expoId: selectedExpo?.value || undefined,
        managerId: selectedManager?.value || undefined,
      };

      const result = await getStallBookedList(params);
      if (result.status) {
        const data = result.response;
        setBookings(data.data || []);
        setCounts(data.counts || { total: 0, full: 0, partial: 0, pending: 0 });
        setTotalItems(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching stall bookings:", error);
      toast.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        expoId: selectedExpo?.value || undefined,
        managerId: selectedManager?.value || undefined,
      };

      const result = await downloadStallBookedExcel(params);

      if (result.status) {
        const url = window.URL.createObjectURL(new Blob([result.response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Stall_Booking_List_${new Date().toLocaleDateString()}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Excel downloaded successfully");
      }
    } catch (error) {
      console.error("Error exporting excel:", error);
      toast.error("Failed to export excel");
    }
  };

  const handleClearFilters = () => {
    setSelectedExpo(null);
    setSelectedManager(null);
    setSearchTerm("");
    setCurrentPage(0);
  };

  const hasActiveFilters =
    selectedExpo || selectedManager || searchTerm;

  useEffect(() => {
    fetchBookings();
  }, [currentPage, searchTerm, selectedExpo, selectedManager, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleCloseModal = () => {
    setSelectedBookingForView(null);
    setBookingPayments([]);
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background:
            "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
        }}
      >
        <h6 className="text-white mb-0 fw-bold">All Stall Booking List</h6>

      </div>

      {/* Filter Bar */}
      <div
        className="px-24 py-16 border-bottom"
        style={{ backgroundColor: "#fafafa" }}
      >
        <div className="d-flex align-items-end flex-wrap gap-3">

          {/* Expo Name Dropdown */}
          <div style={{ minWidth: "220px", flex: "1 1 220px", maxWidth: "280px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Expo Name
            </label>
            <Select
              options={expoOptions}
              value={selectedExpo}
              onChange={(val) => {
                setSelectedExpo(val);
                setCurrentPage(0);
              }}
              isClearable
              placeholder="All Expos"
              styles={selectStyles()}
            />
          </div>

          {/* Manager Dropdown */}
          <div style={{ minWidth: "220px", flex: "1 1 220px", maxWidth: "280px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Manager
            </label>
            <Select
              options={managerOptions}
              value={selectedManager}
              onChange={(val) => {
                setSelectedManager(val);
                setCurrentPage(0);
              }}
              isClearable
              placeholder="All Managers"
              styles={selectStyles()}
            />
          </div>

          {/* Search */}
          <div style={{ minWidth: "200px", flex: "1 1 200px", maxWidth: "260px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Search
            </label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control radius-8 ps-40"
                style={{ height: "44px" }}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute top-50 translate-middle-y text-secondary-light"
                style={{ fontSize: "18px", left: "12px" }}
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="d-flex align-items-end">
            <button
              className="btn btn-outline-danger h-44-px radius-8 fw-bold d-flex align-items-center justify-content-center gap-2 px-24"
              onClick={handleClearFilters}
              title="Clear Filters"
              style={{ height: "44px" }}
            >
              <Icon icon="solar:restart-bold" className="text-xl" />
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive border radius-12 shadow-sm mb-4">
          <table className="table bordered-table sm-table mb-0 text-nowrap" style={{ minWidth: '1000px' }}>
            <thead>
              <tr
                style={{
                  background: "linear-gradient(135deg, #bb0007, #ed1c24)",
                }}
              >
                <th
                  scope="col"
                  style={{ color: "white", fontWeight: "bold" }}
                  className="px-24"
                >
                  S.No
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Exhibition Name
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Company Name
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Key Person
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Mobile Number
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Stall No
                </th>
                <th
                  scope="col"
                  style={{ color: "white", fontWeight: "bold" }}
                  className="text-center"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-48">
                    <div
                      className="spinner-border text-danger"
                      role="status"
                    ></div>
                    <div className="mt-12 text-secondary-light fw-medium">
                      Loading bookings...
                    </div>
                  </td>
                </tr>
              ) : bookings.length > 0 ? (
                bookings.map((item, index) => (
                  <tr key={item._id} className="hover-bg-light transition-all">
                    <td className="px-24 fw-medium text-secondary-light">
                      {currentPage * itemsPerPage + index + 1}
                    </td>
                    <td>
                      {item.expoId?.expoName || item.expoId?.name || "-"}
                    </td>
                    <td>
                      {item.companyName || "-"}
                    </td>
                    <td className="text-secondary-light">
                      {item.name || "-"}
                    </td>
                    <td className="text-secondary-light">
                      {item.mobileNo || item.phone || "-"}
                    </td>
                    <td>
                      <div className="fw-bold text-secondary-light" style={{ fontSize: '14px' }}>
                        {item.stallNo || item.stallDetails || "N/A"}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center align-items-center">
                        <button
                          type="button"
                          className="btn-view-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                          style={{
                            backgroundColor: "#eef2ff",
                            color: "#4338ca",
                            border: "1px solid #e0e7ff",
                          }}
                          onClick={async () => {
                            setSelectedBookingForView(item);
                            try {
                              const res = await getClientFollowupPayments(item._id);
                              if (res.status) {
                                setBookingPayments(res.response?.data || res.response || []);
                              } else {
                                setBookingPayments([]);
                              }
                            } catch (err) {
                              console.error("Failed to load payments:", err);
                              setBookingPayments([]);
                            }
                          }}
                          title="View Details"
                        >
                          <Icon icon="lucide:eye" className="menu-icon" style={{ fontSize: "16px" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-48">
                    <div className="text-secondary-light fw-medium">
                      No bookings found.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-24">
          {hasPermission("stall_booking_list", "view") && (
            <button
              className="btn btn-success d-flex align-items-center gap-2 px-24 py-10 radius-8 shadow-sm"
              onClick={handleExportExcel}
            >
              <Icon icon="lucide:file-spreadsheet" className="text-xl" />
              Export Excel
            </button>
          )}

          <TablePagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            onPageChange={handlePageChange}
            rowsPerPage={itemsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalRecords={totalItems}
          />
        </div>
      </div>

      {/* Detailed View Modal */}
      {selectedBookingForView && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1100, backdropFilter: "blur(4px)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content radius-16 border-0 shadow-2xl overflow-hidden">
              {/* Modal Header */}
              <div
                className="modal-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
                style={{
                  background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
                }}
              >
                <h6 className="text-white mb-0 fw-bold d-flex align-items-center gap-2">
                  <Icon icon="solar:shop-bold" className="text-xl" />
                  Stall Booking Details
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCloseModal}
                  aria-label="Close"
                  style={{ filter: "brightness(0) invert(1)" }}
                ></button>
              </div>

              {/* Modal Body */}
              <div className="modal-body p-24" style={{ backgroundColor: "#fdfdfd" }}>
                <div className="row g-4">
                  {/* Exhibition Details Section */}
                  <div className="col-12">
                    <div className="p-16 radius-12 border bg-white shadow-sm">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                        <Icon icon="solar:calendar-date-bold" />
                        Exhibition Information
                      </h6>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Expo Name</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.expoId?.expoName || selectedBookingForView.expoId?.name || "-"}</span>
                        </div>
                        <div className="col-md-6">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Venue</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.expoId?.location?.address || selectedBookingForView.expoId?.venue || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Exhibitor Details Section */}
                  <div className="col-md-6">
                    <div className="p-16 radius-12 border bg-white shadow-sm h-100">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                        <Icon icon="solar:user-bold" />
                        Exhibitor Information
                      </h6>
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Company Name</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.companyName || "-"}</span>
                        </div>
                        <div>
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Key Person</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.name || "-"}</span>
                        </div>
                        <div>
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Designation</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.designation || "-"}</span>
                        </div>
                        <div>
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Mobile Number</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.mobileNo || selectedBookingForView.phone || "-"}</span>
                        </div>
                        <div>
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Email Address</span>
                          <span className="fw-semibold text-dark text-break">{selectedBookingForView.email || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stall & Commercial Details Section */}
                  <div className="col-md-6">
                    <div className="p-16 radius-12 border bg-white shadow-sm h-100">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                        <Icon icon="solar:wallet-money-bold" />
                        Stall & Commercial Details
                      </h6>
                      <div className="d-flex flex-column gap-3">
                        <div className="row g-2">
                          <div className="col-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Stall No</span>
                            <span className="badge bg-danger text-white px-12 py-6 fw-bold" style={{ fontSize: "13px" }}>
                              {selectedBookingForView.stallNo || selectedBookingForView.stallDetails || "N/A"}
                            </span>
                          </div>
                          <div className="col-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Stall Type</span>
                            <span className="fw-semibold text-dark">{selectedBookingForView.stallType?.name || "-"}</span>
                          </div>
                        </div>
                        <div className="row g-2">
                          <div className="col-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Area (SQM)</span>
                            <span className="fw-semibold text-dark">
                              {selectedBookingForView.sqm ? `${selectedBookingForView.sqm} sqm` : (selectedBookingForView.stallDetails || "-")}
                            </span>
                          </div>
                          <div className="col-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Total Amount</span>
                            <span className="fw-semibold text-dark">₹{(selectedBookingForView.totalAmount || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                        <div className="row g-2">
                          <div className="col-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Discount</span>
                            <span className="fw-semibold text-dark">₹{(selectedBookingForView.discount || 0).toLocaleString('en-IN')}</span>
                          </div>
                          <div className="col-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Net Amount</span>
                            <span className="fw-bold text-success" style={{ fontSize: "15px" }}>₹{(selectedBookingForView.amount || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                        {selectedBookingForView.products && selectedBookingForView.products.length > 0 && (
                          <div>
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-2">Products Interested</span>
                            <span className="fw-semibold text-dark text-break">
                              {selectedBookingForView.products.map((p) => p.productName || p.name || p).join(", ")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="col-12">
                    <div className="p-16 radius-12 border bg-white shadow-sm">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                        <Icon icon="solar:map-point-bold" />
                        Address Details
                      </h6>
                      <p className="mb-0 text-dark fw-medium">
                        {[
                          selectedBookingForView.address,
                          selectedBookingForView.city,
                          selectedBookingForView.state,
                          selectedBookingForView.pincode
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </p>
                    </div>
                  </div>

                  {/* Audit Logs Section */}
                  <div className="col-12">
                    <div className="p-16 radius-12 border bg-white shadow-sm">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                        <Icon icon="solar:notes-bold" />
                        Audit Information
                      </h6>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Created / Blocked By</span>
                          <span className="fw-semibold text-dark">{selectedBookingForView.manager?.name || "-"}</span>
                        </div>
                        <div className="col-md-4">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Blocked Date / Time</span>
                          <span className="fw-semibold text-dark">
                            {selectedBookingForView.blockedDate
                              ? new Date(selectedBookingForView.blockedDate).toLocaleString()
                              : selectedBookingForView.createdAt
                                ? new Date(selectedBookingForView.createdAt).toLocaleString()
                                : "-"}
                          </span>
                        </div>
                        <div className="col-md-4">
                          <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Booked Date / Time</span>
                          <span className="fw-semibold text-dark">
                            {selectedBookingForView.bookedTime
                              ? new Date(selectedBookingForView.bookedTime).toLocaleString()
                              : selectedBookingForView.updatedAt
                                ? new Date(selectedBookingForView.updatedAt).toLocaleString()
                                : "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Details Section */}
                  <div className="col-12">
                    <div className="p-16 radius-12 border bg-white shadow-sm">
                      <h6 className="text-danger fw-bold mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                        <Icon icon="solar:wallet-money-bold" />
                        Stall Payment History
                      </h6>
                      <div className="table-responsive border radius-8 shadow-sm scroll-sm" style={{ maxHeight: "200px", overflowY: "auto" }}>
                        <table className="table table-sm mb-0 align-middle">
                          <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr>
                              <th className="text-xs fw-bold px-12 py-8">S.No</th>
                              <th className="text-xs fw-bold px-12 py-8">Date</th>
                              <th className="text-xs fw-bold px-12 py-8">Method</th>
                              <th className="text-xs fw-bold px-12 py-8">Transaction ID / Details</th>
                              <th className="text-xs fw-bold px-12 py-8 text-end">Amount</th>
                              <th className="text-xs fw-bold px-12 py-8">Remarks</th>
                              <th className="text-xs fw-bold px-12 py-8 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bookingPayments && bookingPayments.length > 0 ? (
                              bookingPayments.map((p, idx) => (
                                <tr key={idx}>
                                  <td className="text-xs px-12 py-8">{idx + 1}</td>
                                  <td className="text-xs px-12 py-8">{formatDate(p.createdAt)}</td>
                                  <td className="text-xs px-12 py-8 fw-semibold text-dark">{p.paymentMethod}</td>
                                  <td className="text-xs px-12 py-8">
                                    {p.paymentMethod === 'Cheque' ? (
                                      <div className="d-flex flex-column" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                        <span><strong>Cheque No:</strong> {p.transactionId || "—"}</span>
                                        <span><strong>Bank:</strong> {p.bankName || "—"}</span>
                                        <span><strong>Branch:</strong> {p.branchName || "—"}</span>
                                        {p.proofUrl && (
                                          <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline mt-4 d-inline-block">
                                            View Cheque Copy
                                          </a>
                                        )}
                                      </div>
                                    ) : p.proofUrl ? (
                                      <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline">
                                        View Proof
                                      </a>
                                    ) : (
                                      p.transactionId || "—"
                                    )}
                                  </td>
                                  <td className="text-xs px-12 py-8 text-end fw-bold text-success">
                                    ₹{(p.amountPaid || 0).toLocaleString('en-IN')}
                                  </td>
                                  <td className="text-xs px-12 py-8" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{p.remarks || "—"}</td>
                                  <td className="text-xs px-12 py-8 text-center">
                                    <span className={`badge px-8 py-4 radius-4 text-xs ${p.paymentStatus === 'Completed' ? 'bg-success-100 text-success-800' :
                                      p.paymentStatus === 'Pending' ? 'bg-warning-100 text-warning-800' : 'bg-danger-100 text-danger-800'
                                      }`}>
                                      {p.paymentStatus}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center py-16 text-secondary-light">
                                  No payments found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Timeline History Section */}
                  {selectedBookingForView.statusDetails && selectedBookingForView.statusDetails.length > 0 && (
                    <div className="col-12">
                      <div className="p-16 radius-12 border bg-white shadow-sm">
                        <h6 className="text-danger fw-bold mb-16 border-bottom pb-8 d-flex align-items-center gap-2">
                          <Icon icon="solar:history-bold" />
                          Follow-up & Booking Status History
                        </h6>
                        <div className="timeline-container ps-16 position-relative" style={{ borderLeft: "2px solid #ed1c24" }}>
                          {selectedBookingForView.statusDetails.map((history, idx) => (
                            <div key={idx} className="timeline-item mb-20 position-relative" style={{ paddingLeft: "15px" }}>
                              <span
                                className="position-absolute rounded-circle"
                                style={{
                                  left: "-22px",
                                  top: "6px",
                                  width: "12px",
                                  height: "12px",
                                  backgroundColor: "#ed1c24",
                                  border: "2px solid #fff",
                                  boxShadow: "0 0 0 2px #ed1c24"
                                }}
                              ></span>
                              <div className="timeline-content">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                  <span className="badge bg-danger-focus text-danger-main fw-bold uppercase px-8 py-4" style={{ fontSize: "11px" }}>
                                    {history.status}
                                  </span>
                                  <span className="text-xs text-secondary-light">
                                    {history.createdAt || history.date ? new Date(history.createdAt || history.date).toLocaleString() : "-"}
                                  </span>
                                </div>
                                {history.remarks && (
                                  <p className="mt-8 mb-0 text-sm text-secondary-light bg-light p-8 radius-8 italic">
                                    "{history.remarks}"
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer border-top py-12 px-24 bg-light">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 py-10 fw-bold"
                  onClick={handleCloseModal}
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StallBookingListLayer;
